// eslint-disable-next-line no-restricted-imports
import {
  Configuration,
  CreateOrder201Response,
  DefaultApiGenerateConfirmationCodeRequest,
  DefaultApiGetExchangeRateRequest,
  DefaultApiGetExchangeSettlementDateRequest,
  DefaultApiGetFuturesInitialMarginRequest,
  DefaultApiGetMaxOpenPositionRequest,
  GetExchangeRateResponse,
  GetExchangeSettlementDateResponse,
  GetFuturesInitialMarginResponse,
  GetMaxOpenPositionResponse,
  OrderCreateRequest,
  DefaultApiOrderConfirmationRequest as OrderManagementApiConfirmationRequest,
  DefaultApi as OrderManagmentApi,
  DefaultApiGetAccountHistoryRequest as OrderManagmentApiGetAccoutHistoryRequest,
  DefaultApiGetOrdersRequest as OrderManagmentApiGetOrderRequest,
  DefaultApiModifyOrderRequest as OrderManagmentApiModifyOrderRequest,
} from '@eih-netbroker/api-order';
import { AxiosInstance, AxiosResponse } from 'axios';
import { IConfigAPI } from '../config';
import { ResourceHeadersOfMultiAccountRequest, ResourceHeadersWithToken } from '../services/useResource';
import { OrderResponse } from '../types/order/createOrderResponse';
import { OrderCostCalculateRequest } from '../types/order/orderCostCalculationRequest';
import { BaseResource } from './base.resource';

export type GetOrderRequestParams = Omit<OrderManagmentApiGetOrderRequest, keyof ResourceHeadersWithToken>;

export type GenerateConfirmationCodeRequestParams = Omit<
  DefaultApiGenerateConfirmationCodeRequest,
  keyof ResourceHeadersWithToken
>;

export type ModifyOrderRequest = Omit<OrderManagmentApiModifyOrderRequest, keyof ResourceHeadersWithToken>;

export type GetHistoryRequest = Omit<
  OrderManagmentApiGetAccoutHistoryRequest,
  keyof ResourceHeadersOfMultiAccountRequest
>;

export type OrderConfirmationRequest = Omit<OrderManagementApiConfirmationRequest, keyof ResourceHeadersWithToken>;

export type OrderMaxOpenPositionRequest = Omit<DefaultApiGetMaxOpenPositionRequest, keyof ResourceHeadersWithToken>;

export type OrderMaxOpenPositionResponse = GetMaxOpenPositionResponse;

export type OrderCreateResponse = CreateOrder201Response;

export type ExchangeSettlementDateRequest = Omit<
  DefaultApiGetExchangeSettlementDateRequest,
  keyof ResourceHeadersWithToken
>;

export type OrderExchangeSettlementDate = GetExchangeSettlementDateResponse;

export type OrderExchangeRateRequest = Omit<DefaultApiGetExchangeRateRequest, keyof ResourceHeadersWithToken>;
export type OrderExchangeRate = GetExchangeRateResponse;

export type FuturesInitialMarginRequest = Omit<
  DefaultApiGetFuturesInitialMarginRequest,
  keyof ResourceHeadersWithToken
>;
export type FuturesInitialMargin = GetFuturesInitialMarginResponse;

export class OrderManagementResource extends BaseResource {
  // Name has to be overridden, since the build job renames it to 'r' and
  // every resource class will have the same name
  // @ts-ignore
  static readonly name = 'OrderManagementResource';
  private orderManagementApi: OrderManagmentApi;

  constructor(axiosInstance: AxiosInstance, basePath: string, configApi: IConfigAPI) {
    super(axiosInstance, basePath, configApi);
    this.orderManagementApi = new OrderManagmentApi(
      new Configuration({ basePath: `${basePath}${configApi.usUrl.oms}` }),
      basePath,
      axiosInstance,
    );
  }

  async createOrder(request: OrderCreateRequest, headers: ResourceHeadersWithToken, signal?: AbortSignal) {
    return this.orderManagementApi.createOrder(
      { orderCreateRequest: request, ...headers, xMFARequired: true },
      { signal },
    );
  }

  async getOrders(params: GetOrderRequestParams, headers: ResourceHeadersOfMultiAccountRequest) {
    return this.orderManagementApi.getOrders({ ...params, ...headers }) as unknown as Promise<
      AxiosResponse<Array<OrderResponse>>
    >;
  }

  async generateConfirmationCode(params: GenerateConfirmationCodeRequestParams, headers: ResourceHeadersWithToken) {
    return this.orderManagementApi.generateConfirmationCode({ ...params, ...headers });
  }

  async costCalculation(request: OrderCostCalculateRequest, headers: ResourceHeadersWithToken) {
    return this.orderManagementApi.costCalculation({ orderCostCalculateRequest: request, ...headers });
  }

  async exportPcsFile(request: OrderCreateRequest, headers: ResourceHeadersWithToken) {
    return this.orderManagementApi.exportPcsFile(
      { orderCostCalculateRequest: request, ...headers },
      {
        responseType: 'blob',
        headers: {
          Accept: 'application/pdf',
        },
      },
    );
  }

  async modifyOrder(request: ModifyOrderRequest, headers: ResourceHeadersWithToken) {
    return this.orderManagementApi.modifyOrder({ ...request, ...headers, xMFARequired: true });
  }

  async cancelOrder(orderId: string, headers: ResourceHeadersWithToken) {
    await this.orderManagementApi.cancelOrder({ orderId, ...headers });
  }

  async cancelAllOrders(headers: ResourceHeadersOfMultiAccountRequest) {
    await this.orderManagementApi.cancelAllOrders({ ...headers });
  }

  async getOrderHistory(request: GetHistoryRequest, headers: ResourceHeadersOfMultiAccountRequest) {
    return this.orderManagementApi.getAccountHistory({ ...request, ...headers });
  }

  async orderConfirmation(request: OrderConfirmationRequest, headers: ResourceHeadersWithToken) {
    await this.orderManagementApi.orderConfirmation({ ...request, ...headers });
  }

  async getMaxOpenPosition(
    request: OrderMaxOpenPositionRequest,
    headers: ResourceHeadersWithToken,
  ): Promise<AxiosResponse<OrderMaxOpenPositionResponse>> {
    return this.orderManagementApi.getMaxOpenPosition({ ...request, ...headers });
  }

  async getExchangeRateForOrder(
    request: OrderExchangeRateRequest,
    headers: ResourceHeadersWithToken,
  ): Promise<AxiosResponse<OrderExchangeRate>> {
    return this.orderManagementApi.getExchangeRate({ ...request, ...headers });
  }

  async getExchangeSettlementDate(
    request: ExchangeSettlementDateRequest,
    headers: ResourceHeadersWithToken,
  ): Promise<AxiosResponse<OrderExchangeSettlementDate>> {
    return this.orderManagementApi.getExchangeSettlementDate({ ...request, ...headers });
  }

  async getFuturesInitialMargin(
    request: FuturesInitialMarginRequest,
    headers: ResourceHeadersWithToken,
  ): Promise<AxiosResponse<FuturesInitialMargin>> {
    return this.orderManagementApi.getFuturesInitialMargin({ ...request, ...headers });
  }

  getVersion() {
    return this.orderManagementApi.getVersion();
  }
}
